/**
 * 白板画布：Canvas 2D 渲染 + 指针绘制。
 *
 * 坐标一律用归一化的 0-1，落笔与呈现在任何分辨率下都一致 ——
 * 老系统用设备坐标，换台机器板书就错位。
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import type { Client } from '../lib/client'
import { hasAnyPerm, Perm } from '../lib/protocol'
import { useImageRepaint } from '../lib/useImageRepaint'
import { useMobileLayout } from '../lib/mobile'
import { IDENTITY, pinchMove, pinchStart, toggleZoomAt, type PinchStart, type Pt, type View } from '../lib/pinchZoom'
import { canInsert, insertImageAt } from '../lib/wbInsert'
import {
  boundsOf, cornersOf, drawGrid, drawObject, drawSelection,
  HANDLE_HIT, isResizable, resizeBoxByCorner,
} from '../lib/wbRender'
import type { WBObject, WBPoint } from '../lib/whiteboard-types'
import { useRoomStore } from '../stores/roomStore'
import { useWhiteboardStore } from '../stores/whiteboardStore'

interface Props {
  client: Client
}

export function Whiteboard({ client }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  const [textAt, setTextAt] = useState<WBPoint | null>(null)
  const [textValue, setTextValue] = useState('')

  const pages = useWhiteboardStore((s) => s.pages)
  const currentPage = useWhiteboardStore((s) => s.currentPage)
  const draft = useWhiteboardStore((s) => s.draft)
  const selectedIds = useWhiteboardStore((s) => s.selectedIds)
  const tool = useWhiteboardStore((s) => s.tool)
  const dragOffset = useWhiteboardStore((s) => s.dragOffset)

  // 选中后的移动与缩放。协议和服务端一直支持 move / modify，
  // 只是没接到界面上 —— 图拖进来落在哪就是哪，位置不合适只能删了重拖。
  type Gesture =
    | { type: 'move'; start: WBPoint }
    | { type: 'resize'; objId: number; corner: number; start: WBPoint }
  const [gesture, setGesture] = useState<Gesture | null>(null)
  const [resizeTo, setResizeTo] = useState<WBPoint | null>(null)
  // 拖文件进来时的状态。
  const [dropping, setDropping] = useState(false)
  const [inserting, setInserting] = useState('')

  // 当前页的图片加载完后要重绘一次。
  const repaint = useImageRepaint(pages.find((p) => p.index === currentPage)?.objects)

  const selfId = useRoomStore((s) => s.selfId)
  const setToast = useRoomStore((s) => s.setToast)
  const members = useRoomStore((s) => s.members)
  const me = members.get(selfId)
  const canDraw = !!me && hasAnyPerm(me.prop, Perm.Whiteboard | Perm.OpWB)
  const mobile = useMobileLayout()

  // 画布尺寸跟随容器，保持 4:3 —— 与老系统课件页比例一致。
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return

    const measure = () => {
      const rect = el.getBoundingClientRect()
      const w = rect.width
      setSize({ w, h: Math.min(rect.height, (w * 3) / 4) })
    }

    // 先主动量一次，不要只等 ResizeObserver 的首次回调。
    // 那个回调挂在渲染循环上，页面若一时拿不到渲染帧（后台标签页、
    // 某些无头环境）就一直不来，而白板尺寸停在 0 的表现是
    // 整块画布不可用又没有任何报错，极难看出问题出在哪。
    measure()

    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  /** 当前页里 id 对应的图元。 */
  const objById = useCallback(
    (id: number) => pages.find((p) => p.index === currentPage)?.objects.find((o) => o.id === id),
    [pages, currentPage],
  )

  /** 点是否落在某个图元的包围盒内。 */
  const hitObject = useCallback(
    (id: number, at: WBPoint) => {
      const o = objById(id)
      const b = o && size.w ? boundsOf(o, size.w, size.h) : null
      if (!b) return false
      const [x0, y0, x1, y1] = b
      const px = at.x * size.w
      const py = at.y * size.h
      return px >= x0 && px <= x1 && py >= y0 && py <= y1
    },
    [objById, size],
  )

  /** 点是否压在某个选中图元的缩放手柄上，是则返回是哪个角。 */
  const handleAt = useCallback(
    (at: WBPoint): { objId: number; corner: number } | null => {
      if (!size.w) return null
      const px = at.x * size.w
      const py = at.y * size.h
      for (const id of selectedIds) {
        const o = objById(id)
        if (!o || !isResizable(o)) continue
        const b = boundsOf(o, size.w, size.h)
        if (!b) continue
        const cs = cornersOf(b[0], b[1], b[2], b[3])
        for (let i = 0; i < cs.length; i++) {
          const [hx, hy] = cs[i]!
          if (Math.abs(px - hx) <= HANDLE_HIT / 2 && Math.abs(py - hy) <= HANDLE_HIT / 2) {
            return { objId: id, corner: i }
          }
        }
      }
      return null
    },
    [selectedIds, objById, size],
  )

  /** 拖某个角到新位置后的新框。算法在 wbRender 里，那边可以单测。 */
  const resizedBox = useCallback(
    (objId: number, corner: number, to: WBPoint): [WBPoint, WBPoint] | null =>
      resizeBoxByCorner(objById(objId)?.points ?? [], corner, to),
    [objById],
  )

  // 重绘。
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || size.w === 0) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = size.w * dpr
    canvas.height = size.h * dpr
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(dpr, dpr)

    ctx.fillStyle = '#fbfbf9'
    ctx.fillRect(0, 0, size.w, size.h)
    drawGrid(ctx, size.w, size.h)

    const page = pages.find((p) => p.index === currentPage)
    const objs = [...(page?.objects ?? [])].sort((a, b) => a.z - b.z)
    // 拖动 / 缩放中的图元按预览位置画，让画面跟手 ——
    // 真正的改动等松手才发出去，中途只是本地看着动。
    const preview = (o: WBObject): WBObject => {
      if (dragOffset && selectedIds.includes(o.id)) {
        return {
          ...o,
          points: (o.points ?? []).map((pt) => ({ x: pt.x + dragOffset.x, y: pt.y + dragOffset.y })),
        }
      }
      if (gesture?.type === 'resize' && gesture.objId === o.id && resizeTo) {
        const box = resizedBox(o.id, gesture.corner, resizeTo)
        if (box) return { ...o, points: box }
      }
      return o
    }
    for (const o of objs) drawObject(ctx, preview(o), size.w, size.h)
    if (draft) drawObject(ctx, draft, size.w, size.h)
    for (const o of objs) {
      if (selectedIds.includes(o.id)) drawSelection(ctx, preview(o), size.w, size.h)
    }
  }, [pages, currentPage, draft, size, repaint, selectedIds, dragOffset, gesture, resizeTo, resizedBox])

  /** 把指针事件换算成归一化坐标。 */
  const toNorm = useCallback(
    (e: React.PointerEvent): WBPoint => {
      const rect = canvasRef.current!.getBoundingClientRect()
      return {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      }
    },
    [],
  )

  // ── 双指缩放（手机）──
  // 手机上看板书的细节必须能放大：桌面有滚轮和大屏，手机没有。
  // 缩放只改 canvas 的 CSS transform，不重绘；toNorm 用的是 getBoundingClientRect，
  // 它拿到的就是变换后的框，所以放大状态下老师落笔的坐标照样是对的。
  const [view, setView] = useState<View>(IDENTITY)
  const pinchRef = useRef<PinchStart | null>(null)
  const lastTapRef = useRef(0)

  const touchPt = (t: React.Touch): Pt => {
    const r = wrapRef.current!.getBoundingClientRect()
    return { x: t.clientX - r.left, y: t.clientY - r.top }
  }
  const onTouchStart = (e: React.TouchEvent) => {
    if (!mobile) return
    if (e.touches.length === 2) {
      pinchRef.current = pinchStart(view, touchPt(e.touches[0]!), touchPt(e.touches[1]!))
      // 第一根手指可能已经开始画了，双指一来就把那笔作废，免得留一道斜线。
      useWhiteboardStore.getState().cancelDraw?.()
      return
    }
    if (e.touches.length === 1) {
      const now = Date.now()
      if (now - lastTapRef.current < 300) {
        setView(toggleZoomAt(view, touchPt(e.touches[0]!), { w: size.w, h: size.h }))
        lastTapRef.current = 0
      } else {
        lastTapRef.current = now
      }
    }
  }
  const onTouchMove = (e: React.TouchEvent) => {
    if (!pinchRef.current || e.touches.length !== 2) return
    e.preventDefault()
    setView(pinchMove(pinchRef.current, touchPt(e.touches[0]!), touchPt(e.touches[1]!), { w: size.w, h: size.h }))
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length < 2) pinchRef.current = null
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (!canDraw || pinchRef.current) return
    const at = toNorm(e)
    const wb = useWhiteboardStore.getState()

    if (tool === 'text') {
      setTextAt(at)
      setTextValue('')
      return
    }
    if (tool === 'eraser') {
      wb.eraseAt(client, at, selfId)
      return
    }
    if (tool === 'select') {
      // 先看是不是按在缩放手柄上 —— 手柄压在选中框边上，
      // 不先判的话点手柄会被当成「点了图元外面」，选中直接被取消。
      const grab = handleAt(at)
      if (grab) {
        e.currentTarget.setPointerCapture(e.pointerId)
        setGesture({ type: 'resize', objId: grab.objId, corner: grab.corner, start: at })
        return
      }
      // 按在已选中的图元上是要拖它，不是重新选。
      // 反过来做的话，多选之后想整体拖动会先被打散成单选。
      const onSelected = wb.selectedIds.some((id) => hitObject(id, at))
      if (!onSelected) {
        // 按住 Shift 或 Ctrl/Cmd 为多选，和常见画图工具一致。
        wb.selectAt(at, e.shiftKey || e.ctrlKey || e.metaKey)
      }
      const ids = useWhiteboardStore.getState().selectedIds
      if (ids.length && ids.some((id) => hitObject(id, at))) {
        e.currentTarget.setPointerCapture(e.pointerId)
        setGesture({ type: 'move', start: at })
      }
      return
    }

    e.currentTarget.setPointerCapture(e.pointerId)
    wb.beginDraw(toolToKind(tool), at, selfId)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const wb = useWhiteboardStore.getState()

    // 拖动 / 缩放中：只改本地预览，松手才发网络。
    // 每移动一像素就发一条的话，一次拖动能打出上百条消息。
    if (gesture) {
      const at = toNorm(e)
      if (gesture.type === 'move') {
        wb.setDragOffset({ x: at.x - gesture.start.x, y: at.y - gesture.start.y })
      } else {
        setResizeTo(at)
      }
      return
    }

    if (!wb.draft) {
      // 橡皮擦按住拖动时连续擦除。
      if (canDraw && tool === 'eraser' && e.buttons === 1) {
        wb.eraseAt(client, toNorm(e), selfId)
      }
      return
    }
    wb.extendDraw(toNorm(e))
  }

  const onPointerUp = () => {
    const wb = useWhiteboardStore.getState()

    if (gesture) {
      if (gesture.type === 'move') {
        const d = wb.dragOffset
        if (d) wb.moveSelected(client, d)
        wb.setDragOffset(null)
      } else if (resizeTo) {
        const box = resizedBox(gesture.objId, gesture.corner, resizeTo)
        if (box) wb.resizeObject(client, gesture.objId, box)
      }
      setGesture(null)
      setResizeTo(null)
      return
    }

    if (wb.draft) wb.endDraw(client)
  }

  // ── 拖入与粘贴 ──────────────────────────────────────
  //
  // 老系统里往板书中间贴一张图是直接拖进去的。走「导入课件」那条路
  // 会强行新建一页、铺满整页，讲到一半想插张图就得打断节奏。

  /** 把一批文件插到指定位置。多张时依次错开，免得叠成一摞。 */
  const insertFiles = useCallback(
    async (files: File[], at: WBPoint) => {
      const usable = files.filter(canInsert)
      if (!usable.length) {
        setToast('只能拖入图片或 SVG 矢量图')
        return
      }
      setInserting(`正在插入 ${usable.length} 张…`)
      try {
        for (let i = 0; i < usable.length; i++) {
          const off = i * 0.04
          // 画布的真实比例现量现传：它由容器高度决定，不是恒定的 4:3。
          const rect = canvasRef.current?.getBoundingClientRect()
          const aspect = rect && rect.width > 0 ? rect.height / rect.width : 3 / 4
          const r = await insertImageAt(
            client, currentPage, selfId, usable[i]!,
            { x: at.x + off, y: at.y + off },
            aspect,
          )
          if (!r.ok) {
            setToast(r.reason ?? '插入失败')
            break
          }
        }
      } finally {
        setInserting('')
      }
    },
    [client, currentPage, selfId, setToast],
  )

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!canDraw) return
      // 必须拦掉默认行为：不拦的话浏览器会直接打开这张图，
      // 整个教室页面就被替换掉了。
      e.preventDefault()
      e.stopPropagation()
      e.dataTransfer.dropEffect = 'copy'
      setDropping(true)
    },
    [canDraw],
  )

  const onDragLeave = useCallback((e: React.DragEvent) => {
    // 移到子元素上也会触发 dragleave，用坐标判断是不是真的离开了容器。
    const box = wrapRef.current?.getBoundingClientRect()
    if (!box) return setDropping(false)
    const out =
      e.clientX < box.left || e.clientX > box.right || e.clientY < box.top || e.clientY > box.bottom
    if (out) setDropping(false)
  }, [])

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setDropping(false)
      if (!canDraw) {
        setToast('你当前没有白板操作权限')
        return
      }
      const files = [...(e.dataTransfer.files ?? [])]
      if (!files.length) return
      const rect = canvasRef.current?.getBoundingClientRect()
      const at = rect
        ? { x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height }
        : { x: 0.5, y: 0.5 }
      void insertFiles(files, at)
    },
    [canDraw, insertFiles, setToast],
  )

  // 粘贴：截图后直接 Ctrl+V 贴到板上，比存成文件再拖快得多。
  useEffect(() => {
    if (!canDraw) return
    const onPaste = (e: ClipboardEvent) => {
      // 正在文本框里打字时不要抢剪贴板。
      const el = document.activeElement
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return
      const files = [...(e.clipboardData?.files ?? [])].filter(canInsert)
      if (!files.length) return
      e.preventDefault()
      void insertFiles(files, { x: 0.5, y: 0.5 })
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [canDraw, insertFiles])

  const commitText = () => {
    if (textAt && textValue.trim()) {
      useWhiteboardStore.getState().addText(client, textAt, textValue, selfId)
    }
    setTextAt(null)
    setTextValue('')
  }

  return (
    <div
      className={`wb-wrap${dropping ? ' dropping' : ''}${view.scale > 1 ? ' zoomed' : ''}`}
      ref={wrapRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* 拖到画布上方时给一层提示，不然松手前不知道能不能放。 */}
      {dropping && <div className="wb-drop-hint"><span>松手把图放到这里</span></div>}
      {inserting && <div className="wb-drop-hint busy"><span>{inserting}</span></div>}
      <canvas
        ref={canvasRef}
        className="wb-canvas"
        style={{
          width: size.w, height: size.h, cursor: canDraw ? cursorFor(tool) : 'not-allowed',
          transform: view.scale > 1 ? `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})` : undefined,
          transformOrigin: '0 0',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
      {textAt && (
        <input
          className="wb-text-input"
          autoFocus
          value={textValue}
          placeholder="输入文字后回车"
          style={{ left: textAt.x * size.w, top: textAt.y * size.h }}
          onChange={(e) => setTextValue(e.target.value)}
          onBlur={commitText}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitText()
            if (e.key === 'Escape') {
              setTextAt(null)
              setTextValue('')
            }
          }}
        />
      )}
      {!canDraw && <div className="wb-readonly">你当前没有白板操作权限</div>}
    </div>
  )
}

/** 工具映射到图元类型。 */
function toolToKind(t: string): WBObject['kind'] {
  switch (t) {
    case 'line':
      return 'line'
    case 'rect':
      return 'rect'
    case 'ellipse':
      return 'ellipse'
    default:
      return 'pen'
  }
}

/** 工具对应的鼠标指针样式。 */
function cursorFor(t: string): string {
  if (t === 'select') return 'default'
  if (t === 'text') return 'text'
  return 'crosshair'
}
